<template>
  <section class="service-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="top-row">
      <div class="counter">
        {{ equityKpis.length }} {{ tr("indicatorUnit") }}
      </div>

      <select aria-label="Equity axis selector">
        <option>{{ tr("pageTitle") }}</option>
      </select>
    </div>

    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>{{ tr("axisTitle") }} <small>(EQ)</small></h2>

          <p>{{ tr("axisDescription") }}</p>
        </div>

        <div class="axis-score">
          <span>EQ</span>

          <small>{{ tr("crossCuttingLens") }}</small>
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ equityKpis.length }}</b>

          <span>{{ tr("indicatorsCount") }}</span>
        </div>

        <div>
          <b>{{ facilitiesCount }}</b>

          <span>{{ tr("sampleFacilities") }}</span>
        </div>

        <div>
          <b>{{ criticalCount }}</b>

          <span>{{ tr("criticalIndicators") }}</span>
        </div>
      </div>
    </div>

    <div class="service-kpi-grid">
      <article
        v-for="kpi in equityKpis"
        :key="kpi.code"
        class="service-kpi-card"
        :class="{ flag: kpiStatus(kpi) === 'r' }"
        :data-critical-label="tr('criticalFlag')"
      >
        <div class="service-kpi-top">
          <span class="code">{{ kpi.code }}</span>

          <span class="composite">{{ tr("composite") }} ⚙</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="service-kpi-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>

          <span>{{ kpi.unit }}</span>

          <em>{{ tr("standard") }} ≥ {{ kpi.std }}</em>
        </div>

        <div class="gauge">
          <div
            :style="{
              width: achieve(kpi) + '%',

              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="service-kpi-foot">
          <span>
            {{ tr("achievementTowardStandard") }}:

            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div class="axis-mini-chart">
          <h4>{{ kpi.chart.label }}</h4>

          <apexchart
            v-if="chartsReady"
            :type="kpiApex(kpi).type"
            height="240"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`equityPage.${key}`, params);

const chartFont = computed(() => (isArabic.value ? "Cairo" : "Inter"));

const loading = ref(false);

const chartsReady = ref(false);

const eq01Summary = ref({ value: 0, facilities: 0 });

const eq02Summary = ref({ value: 0, facilities: 0 });

const eq03Summary = ref({ value: 0, facilities: 0 });

const eq04Summary = ref({ value: 0, facilities: 0 });

const eq01Components = ref([]);

const eq02Components = ref([]);

const eq03Components = ref([]);

const eq04Components = ref([]);

const statusLabel = computed(() => ({
  g: tr("status.compliant"),

  a: tr("status.near"),

  r: tr("status.gap"),
}));

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const getPayload = (res, fallback = {}) =>
  res?.data?.data ?? res?.data ?? fallback;

const fetchEquityData = async () => {
  try {
    loading.value = true;

    chartsReady.value = false;

    const [
      eq01SummaryRes,

      eq01ComponentsRes,

      eq02SummaryRes,

      eq02ComponentsRes,

      eq03SummaryRes,

      eq03ComponentsRes,

      eq04SummaryRes,

      eq04ComponentsRes,
    ] = await Promise.all([
      api.get("/health-facilities/eq01Summary"),

      api.get("/health-facilities/eq01Components"),

      api.get("/health-facilities/eq02Summary"),

      api.get("/health-facilities/eq02Components"),

      api.get("/health-facilities/eq03Summary"),

      api.get("/health-facilities/eq03Components"),

      api.get("/health-facilities/eq04Summary"),

      api.get("/health-facilities/eq04Components"),
    ]);

    eq01Summary.value = getPayload(eq01SummaryRes, { value: 0, facilities: 0 });

    eq02Summary.value = getPayload(eq02SummaryRes, { value: 0, facilities: 0 });

    eq03Summary.value = getPayload(eq03SummaryRes, { value: 0, facilities: 0 });

    eq04Summary.value = getPayload(eq04SummaryRes, { value: 0, facilities: 0 });

    eq01Components.value = getPayload(eq01ComponentsRes, []);

    eq02Components.value = getPayload(eq02ComponentsRes, []);

    eq03Components.value = getPayload(eq03ComponentsRes, []);

    eq04Components.value = getPayload(eq04ComponentsRes, []);
  } catch (error) {
    console.error("Equity API Error:", error);
  } finally {
    chartsReady.value = true;

    loading.value = false;
  }
};

const componentName = (item) => {
  if (isArabic.value) {
    return (
      item?.component_ar ||
      item?.criterion_ar ||
      item?.label_ar ||
      item?.label ||
      item?.name ||
      tr("unspecified")
    );
  }

  return (
    item?.component_en ||
    item?.criterion_en ||
    item?.label_en ||
    item?.label ||
    item?.name ||
    tr("unspecified")
  );
};

const componentValue = (item) => Number(item?.avg_score || 0);

const valueFromSummary = (summary) => Number(summary?.value || 0);

const facilitiesCount = computed(() => {
  return (
    Number(eq01Summary.value?.facilities || 0) ||
    Number(eq02Summary.value?.facilities || 0) ||
    Number(eq03Summary.value?.facilities || 0) ||
    Number(eq04Summary.value?.facilities || 0) ||
    0
  );
});

const equityKpis = computed(() => [
  {
    code: "EQ-01",

    name: tr("kpis.eq01.name"),

    val: valueFromSummary(eq01Summary.value),

    unit: "%",

    std: 70,

    chart: {
      type: "bar",

      label: tr("kpis.eq01.chartLabel"),

      labels: eq01Components.value.map(componentName),

      data: eq01Components.value.map(componentValue),
    },
  },

  {
    code: "EQ-02",

    name: tr("kpis.eq02.name"),

    val: valueFromSummary(eq02Summary.value),

    unit: "/100",

    std: 60,

    chart: {
      type: "radialBar",

      label: tr("kpis.eq02.chartLabel"),

      value: valueFromSummary(eq02Summary.value),
    },
  },

  {
    code: "EQ-03",

    name: tr("kpis.eq03.name"),

    val: valueFromSummary(eq03Summary.value),

    unit: "/100",

    std: 60,

    chart: {
      type: "bar",

      label: tr("kpis.eq03.chartLabel"),

      labels: eq03Components.value.map(componentName),

      data: eq03Components.value.map(componentValue),
    },
  },

  {
    code: "EQ-04",

    name: tr("kpis.eq04.name"),

    val: valueFromSummary(eq04Summary.value),

    unit: "/100",

    std: 60,

    chart: {
      type: "radialBar",

      label: tr("kpis.eq04.chartLabel"),

      value: valueFromSummary(eq04Summary.value),
    },
  },
]);

const kpiStatus = (kpi) => {
  const p = (Number(kpi.val || 0) / Number(kpi.std || 1)) * 100;

  if (p >= 100) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (p > 70) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) =>
  Math.min(
    100,

    Math.round((Number(kpi.val || 0) / Number(kpi.std || 1)) * 100)
  );

const criticalCount = computed(
  () => equityKpis.value.filter((kpi) => kpiStatus(kpi) === "r").length
);

const colorByValue = (value) => {
  const v = Number(value || 0);

  if (v < 50) return "#ef4444";

  if (v < 70) return "#f59e0b";

  return "#10b981";
};

const radialOptions = (value) => ({
  chart: {
    type: "radialBar",

    toolbar: { show: false },

    fontFamily: chartFont.value,
  },

  colors: [colorByValue(value)],

  plotOptions: {
    radialBar: {
      startAngle: -90,

      endAngle: 90,

      hollow: { size: "62%" },

      track: { background: "#e2e8f0" },

      dataLabels: {
        name: { show: false },

        value: {
          offsetY: -4,

          fontSize: "30px",

          fontWeight: 900,

          color: colorByValue(value),

          formatter: () => `${Number(value || 0).toFixed(1)}%`,
        },
      },
    },
  },

  stroke: { lineCap: "butt" },
});

const barOptions = (labels = [], data = []) => ({
  chart: {
    type: "bar",

    toolbar: { show: false },

    fontFamily: chartFont.value,

    foreColor: "#64748b",
  },

  plotOptions: {
    bar: {
      horizontal: false,

      borderRadius: 6,

      columnWidth: "45%",

      distributed: true,
    },
  },

  colors: data.map(colorByValue),

  dataLabels: { enabled: false },

  grid: { borderColor: "#e2e8f0" },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: {
      rotate: -45,

      trim: true,

      style: { fontSize: "11px" },
    },
  },

  yaxis: {
    min: 0,

    max: 100,
  },

  tooltip: {
    y: {
      formatter: (val) => `${Number(val || 0).toFixed(1)}%`,
    },
  },

  legend: { show: false },
});

const kpiApex = (kpi) => {
  const chart = kpi.chart;

  if (chart.type === "radialBar") {
    return {
      type: "radialBar",

      series: [Number(chart.value || 0)],

      options: radialOptions(Number(chart.value || 0)),
    };
  }

  return {
    type: "bar",

    series: [{ name: kpi.code, data: chart.data || [] }],

    options: barOptions(chart.labels || [], chart.data || []),
  };
};

onMounted(fetchEquityData);
</script>

<style scoped>
.service-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Inter", "Cairo", sans-serif;
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #be185d;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #be185d;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;

  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  margin-bottom: 8px;

  color: #061226;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.top-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 18px;
}

.counter {
  color: #64748b;

  font-size: 14px;
}

.top-row select {
  height: 50px;

  width: 285px;

  background: #fff;

  border: 1px solid #cbd5e1;

  border-radius: 8px;

  padding: 0 14px;

  font-family: inherit;

  outline: none;
}

.axis-summary {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  padding: 18px;

  margin-bottom: 18px;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;

  justify-content: space-between;

  gap: 20px;

  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;

  font-weight: 900;

  color: #be185d;

  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;

  font-size: 13px;
}

.axis-score {
  min-width: 130px;

  text-align: center;

  background: #fdf2f8;

  color: #be185d;

  border-radius: 18px;

  padding: 18px;
}

.axis-score span {
  display: block;

  font-size: 34px;

  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 12px;

  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 14px;

  text-align: center;
}

.axis-stats b {
  display: block;

  font-size: 26px;

  color: #be185d;
}

.axis-stats span {
  font-size: 13px;

  font-weight: 800;

  color: #64748b;
}

.service-kpi-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.service-kpi-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.service-kpi-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.service-kpi-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.service-kpi-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.code {
  font-weight: 900;

  direction: ltr;

  text-align: center;

  white-space: nowrap;

  color: #be185d;

  background: #fdf2f8;

  border: 1px solid #fbcfe8;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 11px;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.service-kpi-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.service-kpi-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.service-kpi-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.service-kpi-value span {
  color: #64748b;
}

.service-kpi-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.service-kpi-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

.axis-mini-chart,
.formula-box {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;

  margin-top: 12px;
}

.axis-mini-chart h4,
.formula-box h4 {
  font-size: 14px;

  font-weight: 900;

  color: #be185d;

  margin: 0 0 10px;
}

.formula-box p {
  margin: 0 0 6px;

  color: #071126;

  font-size: 13px;

  line-height: 1.8;

  font-weight: 800;
}

.formula-box small {
  color: #64748b;

  line-height: 1.8;
}

.count {
  color: #be185d;

  font-weight: 900;
}

.q-row {
  display: flex;

  gap: 10px;

  margin-top: 8px;

  font-size: 13px;

  color: #334155;
}

.q-row b {
  direction: ltr;

  color: #be185d;

  min-width: 38px;
}

/* ApexCharts داخل كروت المؤشرات - نفس ثيم المشروع */

.axis-mini-chart :deep(.apexcharts-text),
.axis-mini-chart :deep(.apexcharts-xaxis-label),
.axis-mini-chart :deep(.apexcharts-yaxis-label) {
  fill: #64748b !important;
  font-size: 10px;

  color: #64748b !important;
}

.axis-mini-chart :deep(.apexcharts-gridline),
.axis-mini-chart :deep(.apexcharts-xaxis-tick),
.axis-mini-chart :deep(.apexcharts-xaxis line) {
  stroke: #e2e8f0 !important;
}

.axis-mini-chart :deep(.apexcharts-tooltip) {
  background: #ffffff !important;

  border: 1px solid #dbe3ee !important;

  color: #071126 !important;

  box-shadow: 0 10px 24px rgba(15, 35, 64, 0.12) !important;
}

.axis-mini-chart :deep(.apexcharts-tooltip-title) {
  background: #f8fafc !important;

  border-bottom: 1px solid #e2e8f0 !important;

  color: #071126 !important;
}

@media (max-width: 1100px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .service-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }
}

/* ================= RTL / LTR ================= */

.service-page[dir="rtl"] {
  direction: rtl;

  font-family: "Cairo", sans-serif;
}

.service-page[dir="ltr"] {
  direction: ltr;

  font-family: "Inter", "Segoe UI", sans-serif;
}

.service-page[dir="rtl"] .page-header,
.service-page[dir="rtl"] .axis-card-header,
.service-page[dir="rtl"] .service-kpi-card,
.service-page[dir="rtl"] .axis-mini-chart {
  text-align: right;
}

.service-page[dir="ltr"] .page-header,
.service-page[dir="ltr"] .axis-card-header,
.service-page[dir="ltr"] .service-kpi-card,
.service-page[dir="ltr"] .axis-mini-chart {
  text-align: left;
}

.service-page[dir="rtl"] .service-kpi-card.flag::after {
  left: 14px;

  right: auto;
}

.service-page[dir="ltr"] .service-kpi-card.flag::after {
  right: 14px;

  left: auto;
}

.service-page[dir="rtl"] .service-kpi-value em {
  margin-right: auto;

  margin-left: 0;
}

.service-page[dir="ltr"] .service-kpi-value em {
  margin-left: auto;

  margin-right: 0;
}

@media (max-width: 768px) {
  .service-page {
    padding: 12px 8px 28px;

    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 20px;

    line-height: 1.5;
  }

  .page-header p {
    font-size: 12px;

    line-height: 1.8;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 10px;
  }

  .top-row select {
    width: 100%;

    height: 46px;
  }

  .axis-summary {
    padding: 13px;

    border-radius: 15px;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;

    gap: 12px;
  }

  .axis-card-header h2 {
    font-size: 18px;
  }

  .axis-score {
    width: 100%;

    min-width: 0;

    padding: 13px;

    border-radius: 14px;
  }

  .axis-score span {
    font-size: 28px;
  }

  .axis-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: 7px;
  }

  .axis-stats div {
    padding: 10px 5px;

    border-radius: 11px;
  }

  .axis-stats b {
    font-size: 21px;
  }

  .axis-stats span {
    font-size: 10px;

    line-height: 1.5;
  }

  .service-kpi-grid {
    grid-template-columns: minmax(0, 1fr);

    gap: 12px;
  }

  .service-kpi-card {
    min-width: 0;

    padding: 13px;
  }

  .service-kpi-card h3 {
    min-height: 0;

    font-size: 14px;

    line-height: 1.7;
  }

  .service-kpi-value {
    flex-wrap: wrap;
  }

  .service-kpi-value strong {
    font-size: 28px;
  }

  .service-kpi-value em {
    width: 100%;

    margin: 4px 0 0 !important;

    text-align: center;
  }

  .service-kpi-foot {
    align-items: flex-start;

    gap: 10px;
  }

  .axis-mini-chart {
    padding: 8px;

    overflow-x: auto;

    -webkit-overflow-scrolling: touch;
  }

  .axis-mini-chart :deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  .loader-card {
    width: calc(100% - 28px);

    padding: 28px 18px;
  }
}

@media (max-width: 380px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }

  .service-kpi-foot {
    flex-direction: column;
  }
}
</style>
